import clsx from 'clsx';
import { PropsWithChildren, ReactNode } from 'react';
import Badge, { BadgeVariants } from '../../molecules/Badge';
import { NACSConnectorIcon } from '../../icons';

interface InfoCardProps {
  title: string;
  body: string;
  icon: ReactNode;
  badge?: {
    variant: BadgeVariants;
    text: string;
  };
  size: 'short' | 'tall';
}

interface ChargingLevelCardProps {
  level: string;
  chargeTime: string;
  connectorType: string;
  description: string;
  ChargerIcon: React.FC<{ className?: string }>;
}

export const InfoCard: React.FC<PropsWithChildren<InfoCardProps>> = ({
  title,
  body,
  icon,
  badge,
  children,
  size,
}) => {
  return (
    <div
      className={clsx(
        'flex flex-1 flex-col justify-between rounded-xsmall border border-neutral-200 bg-neutral-100 p-m',
        { 'gap-xl': size === 'tall', 'gap-m': size === 'short' }
      )}
    >
      <div
        className={clsx('flex flex-col', { 'gap-m': badge, 'gap-l': !badge })}
      >
        {badge && (
          <div>
            <Badge label={badge.text} variant={badge.variant} size="Small" />
          </div>
        )}
        {icon}
        <div className="flex flex-col gap-xs">
          <div className="text-body1Medium text-neutral-800">{title}</div>
          <div
            className={clsx('text-microLight text-neutral-800', {
              'ml:text-body2Light': size === 'tall',
            })}
          >
            {body}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
};

export const ChargingLevelCard: React.FC<ChargingLevelCardProps> = ({
  level,
  chargeTime,
  connectorType,
  description,
  ChargerIcon,
}) => {
  return (
    <div className="flex flex-1 flex-col rounded-xsmall border border-neutral-200 bg-neutral-100 p-m">
      <div className="mb-s">
        <span className="inline-block">
          <Badge label={level} variant={BadgeVariants.Gray} size="Small" />
        </span>
      </div>
      <div className="mb-m grid grid-cols-2 gap-m">
        <div className="items-left flex flex-col">
          <ChargerIcon className="mb-xs size-xl" />
          <span className="mb-xs text-microLight text-neutral-700">
            Charge time
          </span>
          <span className="text-body1Bold text-neutral-900">{chargeTime}</span>
        </div>
        <div className="items-left flex flex-col">
          <NACSConnectorIcon className="mb-xs size-xl" />
          <span className="mb-xs text-microLight text-neutral-700">
            Connector type
          </span>
          <span className="text-body1Bold text-neutral-900">
            {connectorType}
          </span>
        </div>
      </div>
      <div className="mt-auto text-microLight text-neutral-800">
        {description}
      </div>
    </div>
  );
};
